import React from "react";
import { Grid, Typography } from "@sumprio/ui";
import type { SpreadsheetPreviewData } from "./useSpreadsheetPreview";

type SpreadsheetPreviewPanelProps = {
  loading: boolean;
  error: boolean;
  data: SpreadsheetPreviewData | null;
  truncated: boolean;
  classNamePrefix: "view-document-modal" | "view-attachment-modal";
};

export function SpreadsheetPreviewPanel({
  loading,
  error,
  data,
  truncated,
  classNamePrefix,
}: SpreadsheetPreviewPanelProps) {
  if (loading) {
    return (
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding="2xl"
      >
        <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
          Loading…
        </Typography>
      </Grid>
    );
  }

  if (error || data === null) {
    return (
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="md"
        padding="2xl"
        className={`${classNamePrefix}__fallback`}
      >
        <Typography variant="body" bodySize="md" bodyWeight="regular" color="secondary">
          Could not load spreadsheet preview. You can still try downloading.
        </Typography>
      </Grid>
    );
  }

  const columnCount = Math.max(data.headers.length, ...data.rows.map((row) => row.length), 1);

  return (
    <div className={`${classNamePrefix}__spreadsheet-preview`}>
      {data.sheetName ? (
        <Typography
          variant="body"
          bodySize="sm"
          bodyWeight="medium"
          className={`${classNamePrefix}__spreadsheet-sheet-name`}
        >
          {data.sheetName}
        </Typography>
      ) : null}
      <div className={`${classNamePrefix}__spreadsheet-table-wrap`}>
        <table className={`${classNamePrefix}__spreadsheet-table`}>
          <thead>
            <tr>
              {Array.from({ length: columnCount }, (_, index) => (
                <th key={`header-${index}`}>{data.headers[index] ?? ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`}>
                {Array.from({ length: columnCount }, (_, colIndex) => (
                  <td key={`cell-${rowIndex}-${colIndex}`}>{row[colIndex] ?? ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated ? (
        <Typography
          variant="body"
          bodySize="xs"
          bodyWeight="regular"
          color="secondary"
          className={`${classNamePrefix}__spreadsheet-truncated`}
        >
          Preview truncated to the first 500 rows. Download the file to view all data.
        </Typography>
      ) : null}
    </div>
  );
}
